<template>
  <article class="job-detail-item cmn-card" v-if="jobInfo && jobInfo.id">
    <div class="job-inner">
      <div class="position">
        <!-- Top: Avatar & Company Meta -->
        <div class="job-meta-top">
          <div class="job-avatar avatar-logo">
            <img
              v-if="jobInfo.company_logo_path"
              :src="jobInfo.company_logo_path"
              :alt="(jobInfo.company_name || 'Company') + ' Logo'"
              class="avatar-logo-img"
              loading="lazy"
            />
            <img
              v-else
              src="@/assets/images/common/ico_logo.svg"
              :alt="(jobInfo.company_name || 'Company') + ' Logo'"
              class="avatar-logo-img"
              loading="lazy"
            />
          </div>
          <div class="company-name-container">
            <router-link
              v-if="jobInfo.company_id"
              :to="{ name: companyDetailRouteName, params: { id: jobInfo.company_id } }"
              class="company-name-link"
            >
              {{ jobInfo.company_name }}
              <span v-if="jobInfo.agency == 1" class="agency-tag">agency</span>
            </router-link>
            <span v-else class="company-name-link">
              {{ jobInfo.company_name }}
              <span v-if="jobInfo.agency == 1" class="agency-tag">agency</span>
            </span>
            <time class="job-time" v-if="jobInfo.created_at || jobInfo.posted_date">
              {{ jobInfo.created_at || jobInfo.posted_date }}
            </time>
          </div>
        </div>

        <!-- Title & Short Description -->
        <div class="job-info border-bt">
          <h2 class="job-title">{{ jobInfo.job_title }}</h2>
          <p class="job-short-desc" v-if="jobInfo.short_desc || jobInfo.description">
            {{ jobInfo.short_desc || jobInfo.description }}
          </p>
        </div>

        <!-- Job Details Grid -->
        <div class="info-section border-bt">
          <h3 class="info-section-ttl">
            <p>အလုပ်အကိုင်အချက်အလက်များ</p>
          </h3>

          <div class="company-info">
            <!-- Salary -->
            <div class="info-item">
              <app-icon name="dollar" width="24" height="20" />
              <div class="info-text">
                <div class="info-label">လစာ</div>
                <div class="info-value">
                  <!-- Both min and max -->
                  <span class="en" v-if="jobInfo.min_salary && jobInfo.max_salary">
                    {{ formatNumber(jobInfo.min_salary) }} - {{ formatNumber(jobInfo.max_salary) }} MMK
                  </span>
                  <!-- Min salary only -->
                  <span class="en" v-else-if="jobInfo.min_salary">
                    From {{ formatNumber(jobInfo.min_salary) }} MMK
                  </span>
                  <!-- Max salary only -->
                  <span class="en" v-else-if="jobInfo.max_salary">
                    Up to {{ formatNumber(jobInfo.max_salary) }} MMK
                  </span>
                  <!-- None -->
                  <span class="en" v-else>Negotiable</span>
                </div>
              </div>
            </div>

            <!-- Gender -->
            <div class="info-item" v-if="jobInfo.gender">
              <app-icon name="gender" width="21" height="17" />
              <div class="info-text">
                <div class="info-label">အလုပ်လက်ခံမည့် (ကျား/မ)</div>
                <div class="info-value">
                  <span v-if="jobInfo.gender == 1">ကျား</span>
                  <span v-else-if="jobInfo.gender == 2">မ</span>
                  <span v-else>ကျား/မ</span>
                </div>
              </div>
            </div>

            <!-- Job Type -->
            <div class="info-item" v-if="jobInfo.job_type">
              <app-icon name="job-bag" size="28" />
              <div class="info-text">
                <div class="info-label">အလုပ်အမျိုးအစား</div>
                <div class="info-value">
                  <span>{{ formatJobType(jobInfo.job_type) }}</span>
                </div>
              </div>
            </div>

            <!-- Experience -->
            <div class="info-item" v-if="jobInfo.experience">
              <app-icon name="experience" size="24" />
              <div class="info-text">
                <div class="info-label">အတွေ့အကြုံ</div>
                <div class="info-value">
                  <span>{{ jobInfo.experience }}</span>
                </div>
              </div>
            </div>

            <!-- Qualification -->
            <div class="info-item" v-if="jobInfo.qualification">
              <app-icon name="qualification" size="24" />
              <div class="info-text">
                <div class="info-label">ပညာအရည်အချင်း</div>
                <div class="info-value">
                  <span>{{ jobInfo.qualification }}</span>
                </div>
              </div>
            </div>

            <!-- Region & Town -->
            <div class="info-item" v-if="jobInfo.region || jobInfo.town">
              <app-icon name="map-pin" size="24" />
              <div class="info-text">
                <div class="info-label">အလုပ်တည်နေရာ</div>
                <div class="info-value">
                  <span v-if="resolvedRegionName">{{ resolvedRegionName }}</span>
                  <span v-if="resolvedRegionName && jobInfo.town"> ၊ </span>
                  <span v-if="jobInfo.town">{{ jobInfo.town.endsWith('မြို့နယ်') ? jobInfo.town : jobInfo.town + 'မြို့နယ်' }}</span>
                </div>
              </div>
            </div>

            <!-- Address -->
            <div class="info-item" v-if="jobInfo.address">
              <app-icon name="address" size="24" />
              <div class="info-text">
                <div class="info-label">နေရပ်လိပ်စာ</div>
                <div class="info-value">
                  <span class="info-value word-break">{{ jobInfo.address }}</span>
                </div>
              </div>
            </div>

            <!-- Expired Date -->
            <div class="info-item" v-if="jobInfo.expired_date">
              <app-icon name="clock-fill" size="24" />
              <div class="info-text">
                <div class="info-label">လျှောက်လွှာပိတ်မည့်ရက်</div>
                <div class="info-value">
                  <span class="warning-ttl">{{ jobInfo.expired_date }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div class="job-description-container" v-if="jobInfo.description">
          <h2 class="info-section-ttl">
            <p>အလုပ်အကိုင်လိုအပ်ချက်နှင့် လုပ်ငန်းတာဝန်များ</p>
          </h2>
          <p class="jobdetail-description">{{ jobInfo.description }}</p>
        </div>

        <!-- Safe Job Warning Card -->
        <div class="job-info-card warning">
          <div class="warning-header">
            <app-icon name="warning-circle" size="20" class="warning-icon" />
            <h4 class="warning-ttl">လုံခြုံစိတ်ချစွာ အလုပ်လျှောက်ရန်</h4>
          </div>
          <div class="warning-body">
            <p class="warning-desc">
              <strong>သတိပြုရန်:</strong> အလုပ်လျှောက်ထားရာတွင် လည်းကောင်း၊ အင်တာဗျူး ဖြေဆိုရာတွင် လည်းကောင်း မည်သည့်
              အဖိုးအခ (သို့မဟုတ်) အာမခံကြေးကိုမျှ ပေးဆောင်ရန် မလိုပါ။ ကုမ္ပဏီအမည်ကို အလွဲသုံးစားပြု၍ ငွေကြေးတောင်းခံမှု တစ်စုံတစ်ရာ ရှိပါက
              သတိပြုရှောင်ရှားကြပါရန် အကြံပြုအပ်ပါသည်။
            </p>
          </div>
        </div>

        <!-- Action Buttons Slot / Defaults -->
        <div class="action-buttons-wrap" v-if="showActions">
          <slot name="actions">
            <router-link
              v-if="showApplyBtn && jobInfo.id"
              :to="{ name: 'applyIndex', params: { id: jobInfo.id } }"
              class="btn btn-apply"
            >
              <app-icon name="send" size="24" class="btn-icon" />
              <span>အလုပ်လျှောက်ရန်</span>
            </router-link>
          </slot>

          <button
            v-if="showShareBtn"
            @click="shareOnFacebook"
            type="button"
            class="btn btn-share"
            aria-label="Facebook တွင် အလုပ်မျှဝေရန်"
          >
            <app-icon name="share" size="24" />
            <span>မျှဝေရန်</span>
          </button>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import RegionJson from "@/assets/json/region.json";

export default {
  name: "JobDetailContent",
  props: {
    jobInfo: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    regionData: {
      type: [Object, Array],
      default: () => null,
    },
    mode: {
      type: String,
      default: "applicant", // 'applicant' | 'company'
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    showApplyBtn: {
      type: Boolean,
      default: true,
    },
    showShareBtn: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    regionArr() {
      return this.regionData || RegionJson;
    },
    resolvedRegionName() {
      if (!this.jobInfo || !this.jobInfo.region) return "";
      return this.regionArr[this.jobInfo.region] || "";
    },
    companyDetailRouteName() {
      return this.mode === "company" ? "jobPostCompanyDetail" : "jobListingCompanyDetail";
    },
  },
  methods: {
    formatJobType(job_type) {
      const typeMap = {
        '1': 'Full-time',
        '2': 'Part-time',
        '3': 'Internship',
        '4': 'Freelance',
        '5': 'Remote Job',
      };
      return typeMap[String(job_type)] || 'သတ်မှတ်ထားခြင်းမရှိပါ';
    },
    formatNumber(value) {
      return value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
    },
    shareOnFacebook() {
      if (!this.jobInfo || !this.jobInfo.id) return;
      window.open(
        "https://www.facebook.com/sharer/sharer.php?u=https://bib-job.com/job_listing/job_detail/" +
          this.jobInfo.id +
          "&t=" +
          document.title,
        "",
        "menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=300,width=500"
      );
    },
  },
};
</script>

<style src="@/assets/css/job_details.css" scoped></style>

